// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { MediaFile } from '../types';

// Helper function to check if URL needs proxying
const needsProxy = (url: string): boolean => {
  if (!url) return false;
  // Don't proxy Firebase Storage URLs or Sellox CDN URLs
  if (url.includes('firebasestorage') || url.includes('sellox')) return false;
  // Proxy Instagram URLs
  if (url.includes('instagram') || url.includes('cdninstagram')) return true;
  return false;
};

// Helper function to get proxied URL with proper resource fetching
const getProxiedUrl = async (url: string): Promise<string> => {
  if (!url) return '';

  // Skip proxying for Firebase Storage URLs
  if (!needsProxy(url)) {
    return url;
  }

  try {
    const response = await fetch(`https://api.sellox.io/api/proxy?url=${encodeURIComponent(url)}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching proxied resource:', error);
    return url; // Fallback to original URL
  }
};

// Cache for proxied URLs to avoid refetching
const urlCache = new Map<string, string>();

// Helper function to get cached proxied URL
const getCachedProxiedUrl = async (url: string): Promise<string> => {
  if (!url) return '';

  if (urlCache.has(url)) {
    return urlCache.get(url)!;
  }

  const proxiedUrl = await getProxiedUrl(url);
  urlCache.set(url, proxiedUrl);
  return proxiedUrl;
};

interface MediaDisplayProps {
  media: MediaFile | null;
  fallbackUrl?: string;
  itemName?: string;
  mediaDownloaded?: boolean; // Flag to indicate if media is already downloaded
}

export const MediaDisplay: React.FC<MediaDisplayProps> = ({
  media,
  fallbackUrl,
  itemName,
  mediaDownloaded = false
}) => {
  const [proxiedUrl, setProxiedUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  console.log('MediaDisplay rendering:', { media, fallbackUrl });

  // Determine if current media is a video
  const isVideo = media?.type === 'video';
  const rawMediaUrl = media?.downloadURL || fallbackUrl || '';

  // Load proxied URL (for both images AND videos)
  useEffect(() => {
    const loadProxiedUrl = async () => {
      if (!rawMediaUrl) {
        setIsLoading(false);
        return;
      }

      // If media is already downloaded, skip proxying
      if (mediaDownloaded || !needsProxy(rawMediaUrl)) {
        setProxiedUrl(rawMediaUrl);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Proxy both images and videos
        const proxied = await getCachedProxiedUrl(rawMediaUrl);
        setProxiedUrl(proxied);
      } catch (error) {
        console.error('Error loading proxied URL:', error);
        setProxiedUrl(rawMediaUrl); // Fallback to original
      } finally {
        setIsLoading(false);
      }
    };

    loadProxiedUrl();
  }, [rawMediaUrl, mediaDownloaded]);

  const mediaUrl = proxiedUrl || rawMediaUrl;

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-white text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          <p className="opacity-50">Loading media...</p>
        </div>
      </div>
    );
  }

  if (!mediaUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-white text-center">
          <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="opacity-50">No media available</p>
        </div>
      </div>
    );
  }

  if (isVideo) {
    console.log('Rendering video with URL:', mediaUrl);
    console.log('Media object:', media);
    return (
      <video
        src={mediaUrl}
        controls
        className="w-full h-full object-contain"
        playsInline
        preload="metadata"
        onError={(e) => {
          console.error('Video load error:', e);
          console.error('Video URL that failed:', mediaUrl);
        }}
      />
    );
  }

  return (
    <img
      src={mediaUrl}
      alt={itemName || 'Media item'}
      className="w-full h-full object-contain"
      onError={(e) => {
        console.error('Image load error:', mediaUrl);
        e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgMTIwQzEwNS41MjMgMTIwIDExMCAxMTUuNTIzIDExMCAxMTBDMTEwIDEwNC40NzcgMTA1LjUyMyAxMDAgMTAwIDEwMEM5NC40NzcgMTAwIDkwIDEwNC40NzcgOTAgMTEwQzkwIDExNS41MjMgOTQuNDc3IDEyMCAxMDAgMTIwWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
      }}
    />
  );
};
